import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { RoutePaths } from '../../../core/constants/route-paths';
import { DataSessionService } from '../../../core/services/data-session.service';
import { ModalService } from '../../shared/services/modal.service';
import { ModalData } from '../../shared/type/modal';
import { tab, UserSessionData } from '../../../core/types/session-data';
import { LOCAL_LANG } from '../../../core/constants/constants';
import { ContentService } from '../../contents/services/content.service';
import { dataDownloadPWBIResponse } from '../../contents/type/content';
import { PowerbiStateService } from '../../powerbi-embed/services/powerbi-state.service';
import { DataLayerService } from '../../../core/services/data-layer.service';

@Component({
  selector: 'app-header-logged',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule],
  providers: [],
  templateUrl: './header-logged.component.html',
  styleUrl: './header-logged.component.scss',
})
export class HeaderLoggedComponent {
  userData!: UserSessionData | null;
  @Input() lang: string = 'es';
  tabSelected: tab = 'my-global';
  screenName: string = '';

  content: string = '';
  isDownloading: boolean = false;
  showButtonDownload: boolean = false;
  private bookmarkState: string | null = null;

  updateModalData = (updateService: () => void): ModalData => {
    return {
      icon: 'icon-LOG-OUT',
      title: 'logged-header.close-modal.title-close-user',
      subtitle: 'logged-header.close-modal.subtitle',
      leftButton: {
        label: 'logged-header.close-modal.btn-back',
        action: () => {},
      },
      rigthButton: {
        label: 'logged-header.close-modal.btn-accept',
        action: () => updateService(),
      },
    };
  };

  constructor(
    private readonly _router: Router,
    private readonly _dataSessionService: DataSessionService,
    private readonly _contentService: ContentService,
    private readonly _modalService: ModalService,
    private readonly _powerbiStateService: PowerbiStateService,
    private readonly _dataLayerService: DataLayerService
  ) {
    this.userData = this._dataSessionService.userData;
    this.lang = localStorage.getItem(LOCAL_LANG) || this.userData?.lang || 'es';
  }

  ngOnInit(): void {
    let fullPath = this._router.url;

    this._router.events.subscribe((event) => {
      if (event.constructor.name === 'NavigationEnd') {
        fullPath = this._router.url;
        this.tabSelected = this.getTabFromRoute(fullPath);
        this.screenName = fullPath.replace('/', '');
        if (this.screenName === 'my-managements') {
          this.screenName = this._dataSessionService.isValidRole([
            'administrador',
          ])
            ? 'my-managements-users'
            : 'my-managements-requests';
        }
        this.screenName = this.screenName.replace('/', ':');
        this._dataLayerService.pushEventPageView(this.screenName);
        this.getExternalRoute(fullPath);
        if (fullPath.includes('/content-powerbi/')) {
          this.content = fullPath.split('/content-powerbi/')[1];
        }
      }
    });
  }

  ngOnChanges(): void {
    this.userData = this._dataSessionService.userData;
    this.lang = localStorage.getItem(LOCAL_LANG) || this.userData?.lang || 'es';
  }

  changeLanguage(event: CustomEvent) {
    const selectedLanguage = event.detail.value;
    this.lang = selectedLanguage;
    // this._translate.use(selectedLanguage);
    this._dataLayerService.pushEventClickButton(
      '',
      `change-language-${selectedLanguage}`,
      `header.${selectedLanguage}`,
      'header'
    );
    localStorage.setItem(LOCAL_LANG, selectedLanguage);

    window.location.reload();
  }

  async handleRedirect(route: tab): Promise<void> {
    this._dataLayerService.pushEventClickLink(
      this.screenName,
      route,
      `logged-header.${route}`,
      'main menu'
    );
    this.tabSelected = route;
    let link = `${RoutePaths.home}`;
    if (route === 'content') {
      link = `${RoutePaths.contentManag}`;
    } else if (route === 'management') {
      link = `${RoutePaths.managements}`;
    }
    await this._router.navigate([link]);
  }

  getTabFromRoute(route: string): tab {
    if (route.includes(RoutePaths.home)) {
      return 'my-global';
    } else if (
      route.includes(RoutePaths.contentManag) ||
      route.includes(RoutePaths.contentEditor)
    ) {
      return 'content';
    } else if (route.includes(RoutePaths.managements)) {
      return 'management';
    }
    return 'my-global';
  }

  getExternalRoute(route: string): void {
    if (
      route.includes(RoutePaths.changePassword) ||
      route.includes(RoutePaths.updatePassword)
    ) {
      this._dataSessionService.setLoggedIn(false);
    }
    if (route.includes(RoutePaths.contentPowerBI)) {
      this.showButtonDownload = true;
    } else {
      this.showButtonDownload = false;
    }
  }

  logOut(): void {
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'logout',
      'logged-header.close-sesion',
      'header'
    );
    this._modalService.openGenericModal(
      this.updateModalData(() => {
        this._dataSessionService.logOutUser();
      })
    );
  }

  checkRol(value: tab): boolean {
    return this._dataSessionService.canAccessTab(value);
  }

  handleDownload(): void {
    if (!this.content) return;
    this.isDownloading = true;

    this._dataLayerService.pushEventDownload('download-powerbi', 'download');

    this._modalService.showDownloadModalService(() => {});
    this.captureAndDownload();
  }

  /**
   * Captura el bookmark del reporte PowerBI antes de iniciar la descarga
   */
  async captureAndDownload(): Promise<void> {
    try {
      // Capturar el estado del bookmark desde el reporte PowerBI
      this.bookmarkState =
        await this._powerbiStateService.captureBookmarkState();

      if (this.bookmarkState) {
        console.log(
          'Bookmark capturado, iniciando descarga con filtros aplicados'
        );
      } else {
        console.warn(
          'No se pudo capturar el bookmark, descargando sin filtros'
        );
      }

      // Iniciar el proceso de descarga
      this.callDownloadService();
    } catch (error) {
      console.error('Error al capturar bookmark:', error);
      // Continuar con la descarga aunque falle la captura del bookmark
      this.callDownloadService();
    }
  }

  callDownloadService(exportId?: string): void {
    this._contentService
      .downloadPowerBi(this.content, exportId, this.bookmarkState || undefined)
      .subscribe({
        next: (response) => {
          const data: dataDownloadPWBIResponse = response.data;

          if (data.exportId) {
            setTimeout(() => {
              this.callDownloadService(data.exportId);
            }, 5000);
          } else if (data.file && data.contentType && data.contentDisposition) {
            // El archivo está listo, procesarlo y descargarlo
            this._contentService.downloadFile(
              data.file,
              data.contentType,
              data.contentDisposition
            );
            this.isDownloading = false;
            this.bookmarkState = null; // Limpiar el bookmark después de la descarga
          }
        },
        error: (err) => {
          this._dataLayerService.pushEventSubmitError(
            this.screenName,
            'users-report',
            err?.status ?? 'unknown',
            'Error downloading Power BI report'
          );
          this.isDownloading = false;
          this.bookmarkState = null; // Limpiar el bookmark en caso de error
        },
        complete: () => {},
      });
  }
}
